import { useFormik } from "formik";
import { useContext, useEffect } from "react";
import { Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";
import Autocomplete from "../../components/input/autocomplete";
import { heightAutocomplete } from "../../components/input/autocomplete/styleJS";
import TextValidator from "../../components/input/text-validator";
import { RESPONSE_STATUS_CODE, TYPE, VARIABLE_STRING, TYPE_CATEGORY } from "../../constants/moduleConsts";
import { handleBlurDate } from "../../utils/functionUtils";
import { REF_TAB, INIT_TT_DOAN, INIT_TT_DANG, INIT_TT_CHINH_TRI } from "../constants/profileConsts";
import { EmployeeProfile } from "../models/profileModels";
import { addNewPoliticInfo, getPoliticInfo, updatePoliticInfo } from "../services/profileServices";
import { convertDataInfoDto } from "../utils/profileUtils";
import AppContext from "../../../AppContext";
import { searchAllSimpleValue } from "../../services/services";
import useMultiLanguage from "../../../hook/useMultiLanguage";

type IProps = {
  identify: string;
  employeeProfiles: EmployeeProfile;
  isView: boolean;
  formikRef: any;
  handleToggleIsView: () => void;
  activeTab: string;
}

const PoliticsInformation: React.FC<IProps> = ({
  identify,
  employeeProfiles,
  isView,
  formikRef,
  handleToggleIsView,
  activeTab
}) => {
  const { lang } = useMultiLanguage();
  const { setPageLoading } = useContext(AppContext);

  const handleFormSubmit = async (values: any) => {
    try {
      setPageLoading(true);
      const dataSubmit = convertDataInfoDto(values);
      const dataPoliticRes = dataSubmit?.id ? await updatePoliticInfo(dataSubmit?.id, dataSubmit) : await addNewPoliticInfo({ ...dataSubmit, employeeId: identify });
      if (dataPoliticRes?.data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
        toast.success(dataSubmit?.id ? lang("TOAST.EDIT.SUCCESS") : lang("TOAST.ADD.SUCCESS"));
        handleToggleIsView();
        getDataPolitic();
      } else {
        toast.warning(`${dataPoliticRes?.data?.message}`);
      }
    } catch (error) {
      toast.error(lang("GENERAL.ERROR"));
    } finally {
      setPageLoading(false);
    }
  };

  const getDataPolitic = async () => {
    try {
      setPageLoading(true);
      const { data }: any = await getPoliticInfo(identify);
      if (data?.code === RESPONSE_STATUS_CODE.SUCCESS) {
        let values = {
          ...data?.data,
          chucVuDoan: data?.data?.chucVuDoanId ? {
            id: data?.data?.chucVuDoanId,
            value: data?.data?.chucVuDoanText
          } : null
        }
        formik.setValues({
          ...formik.values,
          ...values
        })
      } else {
        toast.warning(data?.message);
      }
    } catch (error) {
      toast.error(lang("GENERAL.ERROR"));
    } finally {
      setPageLoading(false);
    }
  };

  const formik = useFormik({
    initialValues: INIT_TT_CHINH_TRI,
    onSubmit: handleFormSubmit
  });

  useEffect(() => {
    if (!identify || activeTab !== REF_TAB.TT_CHINH_TRI) return;
    getDataPolitic();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [identify, activeTab]);

  useEffect(() => {
    if (formikRef) {
      formikRef.current = formik;
    }
  }, [formik, formikRef]);

  const handleChangeCheckBox = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { checked, name } = event.target;
    switch (name) {
      case VARIABLE_STRING.DOAN_VIEN:
        formik.setValues({
          ...formik.values,
          doanVien: checked,
          ...INIT_TT_DOAN
        });
        break;
      case VARIABLE_STRING.DANG_VIEN:
        formik.setValues({
          ...formik.values,
          ...INIT_TT_DANG,
          dangVien: checked
        });
        break;
      case VARIABLE_STRING.NGOAI_CHI_BO_QUAN_LY:
        formik.setValues({
          ...formik.values,
          chiBoSinhHoat: "",
          hienNayTaiDangBo: "",
          hienNayChucVuDang: "",
          hienNayNgayChuyenDenChiBoHienTai: "",
          ngoaiChiBoQuanLy: checked
        });
        break;
      case VARIABLE_STRING.HIEN_NAY_DA_ROI_DANG:
        formik.setValues({
          ...formik.values,
          hienNayNgayRoiDang: "",
          hienNayLyDoRoiDang: "",
          hienNayDaRoiDang: checked
        });
        break;
      default:
        formik.setFieldValue(name, checked);
        break;
    }
  };

  const handleChangeSelect = (name: string, value: any) => {
    formik.setFieldValue(name, value);
  };

  return (
    <>
      <Form
        className="form-info politics-information"
        onSubmit={formik.handleSubmit}
      >
        <div className="block-content">
          <Row>
            <Col xs={3}>
              <Form.Check
                className="w-100 custom-form-check"
                label={lang("INPUT.UNION.MEMBER")}
                name="doanVien"
                disabled={isView}
                checked={formik.values?.doanVien}
                onChange={handleChangeCheckBox}
              />
            </Col>
            <Col xs={3}>
              <TextValidator
                className="spaces input-politics mt-0"
                type="date"
                lable={lang("INPUT.UNION.JOIN.DATE")}
                name="ngayVaoDoan"
                value={formik.values?.ngayVaoDoan || ""}
                readOnly={isView ? true : formik.values?.doanVien ? false : true}
                onChange={formik.handleChange}
                onBlur={() =>
                  handleBlurDate(
                    formik.setFieldValue,
                    formik.values?.ngayVaoDoan,
                    "ngayVaoDoan"
                  )
                }
              />
            </Col>
            <Col xs={3}>
              <Autocomplete
                styles={heightAutocomplete("29px")}
                lable={lang("INPUT.UNION.ROLE")}
                name="chucVuDoan"
                value={formik.values?.chucVuDoan || null}
                searchFunction={searchAllSimpleValue}
                searchObject={{ type: TYPE_CATEGORY.chucVuDoan }}
                isReadOnly={isView ? true : formik.values?.doanVien ? false : true}
                onChange={(value) => handleChangeSelect(VARIABLE_STRING.CHUC_VU_DOAN, value)}
                options={[]}
                getOptionLabel={(options) => options.value}
              />
            </Col>
            <Col xs={3}>
              <TextValidator
                className="input-politics spaces mt-0"
                lable={lang("INPUT.UNION.JOIN.PLACE")}
                name="noiKetNapDoan"
                value={formik.values?.noiKetNapDoan || ""}
                type="text"
                readOnly={isView ? true : formik.values?.doanVien ? false : true}
                onChange={formik.handleChange}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={3}>
              <Form.Check
                className="w-100 custom-form-check"
                label={lang("INPUT.PARTY.MEMBER")}
                name="dangVien"
                disabled={isView}
                checked={formik.values?.dangVien}
                onChange={handleChangeCheckBox}
              />
            </Col>
          </Row>
          <Row className="spaces mt-10">
            <Col xs={6}>
              <span className="spaces fs-18 fw-600">{lang("INPUT.POLITICAL.PARTY_MEMBER_INFO")}</span>
              <Row>
                <Col xs={12} md={6}>
                  <TextValidator
                    className="input-politics mt-2"
                    lable={lang("INPUT.POLITICAL.PARTY_FILE_NUMBER")}
                    name="soHoSo"
                    value={formik.values.soHoSo}
                    type="text"
                    readOnly={isView || !formik.values?.dangVien}
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <TextValidator
                    className="input-politics mt-2"
                    lable={lang("INPUT.POLITICAL.PARTY_CARD_NUMBER")}
                    name="soThe"
                    value={formik.values.soThe}
                    type="text"
                    readOnly={isView || !formik.values?.dangVien}
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col xs={12}>
                  <TextValidator
                    className="input-politics mt-2"
                    lable={lang("INPUT.POLITICAL.PARTY_POSITION")}
                    name="chucVuDang"
                    value={formik.values.chucVuDang}
                    type="text"
                    readOnly={isView || !formik.values?.dangVien}
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col xs={12}>
                  <TextValidator
                    className="input-politics mt-2"
                    lable={lang("INPUT.POLITICAL_THEORY.NAME")}
                    name="lyLuanChinhTri"
                    value={formik.values.lyLuanChinhTri}
                    type="text"
                    readOnly={isView || !formik.values?.dangVien}
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col xs={12}>
                  <TextValidator
                    className="input-politics mt-2"
                    lable={lang("INPUT.POLITICAL.CARD_ISSUING_PARTY")}
                    name="dangBoCapThe"
                    value={formik.values.dangBoCapThe}
                    type="text"
                    readOnly={isView || !formik.values?.dangVien}
                    onChange={formik.handleChange}
                  />
                </Col>
              </Row>
            </Col>
            <Col xs={6}>
              <span className="spaces fs-18 fw-600">{lang("INPUT.POLITICAL.OFFCIAL_UNION_ADMINSSION")}</span>
              <Row>
              <Col xs={12} md={6}>
                  <TextValidator
                    className="input-politics mt-2"
                    lable={lang("INPUT.POLITICAL.CARD_ISSUANCE_DATE")}
                    name="ngayCapThe"
                    value={formik.values.ngayCapThe}
                    type="date"
                    readOnly={isView || !formik.values?.dangVien}
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <TextValidator
                    className="input-politics mt-2"
                    lable={lang("INPUT.POLITICAL.ADMISSION_DATE")}
                    name="ngayKetNap"
                    value={formik.values.ngayKetNap}
                    type="date"
                    readOnly={isView || !formik.values?.dangVien}
                    onChange={formik.handleChange}
                    onBlur={() =>
                      handleBlurDate(
                        formik.setFieldValue,
                        formik.values?.ngayKetNap,
                        "ngayKetNap"
                      )
                    }
                  />
                </Col>
                <Col xs={12} md={6}>
                  <TextValidator
                    className="input-politics mt-2"
                    lable={lang("INPUT.POLITICAL.PARTY_MOVING_DAY")}
                    name="ngayChuyenDangDi"
                    value={formik.values.ngayChuyenDangDi}
                    type="date"
                    readOnly={isView || !formik.values?.dangVien}
                    onChange={formik.handleChange}
                  />
                </Col>
                <Col xs={12} md={6}>
                  <TextValidator
                    className="input-politics mt-2"
                    lable={lang("INPUT.POLITICAL.PARTY_BACK_DAY")}
                    name="ngayChuyenDangVe"
                    value={formik.values.ngayChuyenDangVe}
                    type="date"
                    readOnly={isView || !formik.values?.dangVien}
                    onChange={formik.handleChange}
                  />
                </Col>
              </Row>
              <TextValidator
                className="input-politics mt-2"
                lable={lang("INPUT.POLITICAL.AT_THE_DIVISION")}
                name="chiBoKetNap"
                value={formik.values.chiBoKetNap}
                type="text"
                readOnly={isView || !formik.values?.dangVien}
                onChange={formik.handleChange}
              />
              <TextValidator
                className="input-politics mt-2"
                lable={lang("INPUT.POLITICAL.AT_THE_PARTY")}
                name="dangBoKetNapDang"
                value={formik.values.dangBoKetNapDang}
                type="text"
                readOnly={isView || !formik.values?.dangVien}
                onChange={formik.handleChange}
              />
            </Col>
          </Row>

          <Row className="spaces mt-10">
            <Col xs={6}>
              <h2>
                {lang(
                  "INPUT.POLITICAL.INFO_ON_RESERE_PARTY_FORMATION"
                )}
              </h2>
              <TextValidator
                className="input-politics"
                lable={lang("INPUT.POLITICAL.ADMISSION_DATE")}
                name="duBiNgayKetNap"
                value={formik.values.duBiNgayKetNap}
                type="date"
                readOnly={isView || !formik.values?.dangVien}
                onChange={formik.handleChange}
                onBlur={() =>
                  handleBlurDate(
                    formik.setFieldValue,
                    formik.values?.duBiNgayKetNap,
                    "duBiNgayKetNap"
                  )
                }
              />
              <TextValidator
                className="input-politics"
                lable={lang("INPUT.POLITICAL.AT_THE_DIVISION")}
                name="duBiChiBo"
                readOnly={isView || !formik.values?.dangVien}
                value={formik.values.duBiChiBo}
                type="text"
                onChange={formik.handleChange}
              />
              <TextValidator
                className="input-politics"
                lable={lang("INPUT.POLITICAL.AT_THE_PARTY")}
                name="duBiTaiDangBo"
                readOnly={isView || !formik.values?.dangVien}
                value={formik.values.duBiTaiDangBo}
                type="text"
                onChange={formik.handleChange}
              />
              <TextValidator
                className="input-politics"
                lable={lang("INPUT.POLITICAL.PRESENTER_FIRST")}
                name="duBiNguoiGioiThieu1"
                value={formik.values.duBiNguoiGioiThieu1}
                type="text"
                readOnly={isView || !formik.values?.dangVien}
                onChange={formik.handleChange}
              />
              <TextValidator
                className="h-auto-input spaces mt-16"
                lable={lang("INPUT.POLITICAL.DOING_WHAT_WHERE")}
                name="duBiThongTinNguoiGioiThieu1"
                value={formik.values.duBiThongTinNguoiGioiThieu1}
                type="text"
                readOnly={isView || !formik.values?.dangVien}
                rows={2}
                onChange={formik.handleChange}
                as={TYPE.TEXTAREA}
              />
              <TextValidator
                className="input-politics"
                lable={lang("INPUT.POLITICAL.PRESENTER_SECEND")}
                name="duBiNguoiGioiThieu2"
                value={formik.values.duBiNguoiGioiThieu2}
                type="text"
                readOnly={isView || !formik.values?.dangVien}
                onChange={formik.handleChange}
              />
              <TextValidator
                className="h-auto-input spaces mt-16"
                lable={lang("INPUT.POLITICAL.DOING_WHAT_WHERE")}
                name="duBiThongTinNguoiGioiThieu2"
                value={formik.values.duBiThongTinNguoiGioiThieu2}
                type="text"
                readOnly={isView || !formik.values?.dangVien}
                as={TYPE.TEXTAREA}
                rows={2}
                onChange={formik.handleChange}
              />
            </Col>
            <Col xs={6}>
              <h2>
                {lang(
                  "INPUT.POLITICAL.PARTY_ADMISSION_INFORMATION_FOR_THE_SECOND"
                )}
              </h2>
              <TextValidator
                className="input-politics"
                lable={lang("INPUT.POLITICAL.ADMISSION_DATE")}
                name="ngayKetNapDangLan2"
                value={formik.values.ngayKetNapDangLan2}
                type="date"
                readOnly={isView || !formik.values?.dangVien}
                onChange={formik.handleChange}
                onBlur={() =>
                  handleBlurDate(
                    formik.setFieldValue,
                    formik.values?.ngayKetNapDangLan2,
                    "ngayKetNapDangLan2"
                  )
                }
              />
              <TextValidator
                className="input-politics"
                lable={lang("INPUT.POLITICAL.AT_THE_DIVISION")}
                name="chiBoKetNapLan2"
                value={formik.values.chiBoKetNapLan2}
                type="text"
                readOnly={isView || !formik.values?.dangVien}
                onChange={formik.handleChange}
              />
              <TextValidator
                className="input-politics"
                lable={lang("INPUT.POLITICAL.AT_THE_PARTY")}
                name="dangBoKetNapDangLan2"
                value={formik.values.dangBoKetNapDangLan2}
                type="text"
                readOnly={isView || !formik.values?.dangVien}
                onChange={formik.handleChange}
              />
              <TextValidator
                className="input-politics"
                lable={lang("INPUT.POLITICAL.PRESENTER")}
                name="lan2NguoiGioiThieu"
                value={formik.values.lan2NguoiGioiThieu}
                type="text"
                readOnly={isView || !formik.values?.dangVien}
                onChange={formik.handleChange}
              />
              <TextValidator
                className="input-politics"
                lable={lang(
                  "INPUT.POLITICAL.DATE_OF_LEAVING_THE_PARTY_FOR_THE_FIRST_TIME"
                )}
                name="ngayRaKhoiDang"
                value={formik.values.ngayRaKhoiDang}
                type="date"
                readOnly={isView || !formik.values?.dangVien}
                onChange={formik.handleChange}
                onBlur={() =>
                  handleBlurDate(
                    formik.setFieldValue,
                    formik.values?.ngayRaKhoiDang,
                    "ngayRaKhoiDang"
                  )
                }
              />
              <TextValidator
                className="h-auto-input spaces mt-16"
                lable={lang(
                  "INPUT.POLITICAL.REASON_FOR_INTERRUPTION"
                )}
                name="lyDoGianDoan"
                value={formik.values.lyDoGianDoan}
                as={TYPE.TEXTAREA}
                rows={2}
                readOnly={isView || !formik.values?.dangVien}
                onChange={formik.handleChange}
              />
            </Col>
          </Row>
          <Row className="spaces mt-10">
            <h2>
              {lang("INPUT.POLITICAL.CURRENT_PARTY_INFORMATION")}
            </h2>
            <Row>
              <Col xs={2}>
                <Form.Check
                  className="w-100 custom-form-check"
                  label={lang(
                    "INPUT.POLITICAL.OUTSIDE_THE_MANAGEMENT_MINISTRY"
                  )}
                  name="ngoaiChiBoQuanLy"
                  disabled={isView || !formik.values?.dangVien}
                  checked={formik.values?.ngoaiChiBoQuanLy}
                  onChange={handleChangeCheckBox}
                />
              </Col>
              {formik.values?.ngoaiChiBoQuanLy && (
                <Col xs={10}>
                  <Row>
                    <Col xs={3}>
                      <TextValidator
                        className="input-politics"
                        lable={lang("INPUT.POLITICAL.PARTY_ACTIVITIES_DIVISION")}
                        name="chiBoSinhHoat"
                        readOnly={isView || !formik.values?.dangVien}
                        value={formik.values.chiBoSinhHoat}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col xs={3}>
                      <TextValidator
                        className="input-politics"
                        lable={lang("INPUT.POLITICAL.AT_THE_PARTY")}
                        name="hienNayTaiDangBo"
                        readOnly={isView || !formik.values?.dangVien}
                        value={formik.values.hienNayTaiDangBo}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col xs={3}>
                      <TextValidator
                        className="input-politics"
                        lable={lang("INPUT.POLITICAL.PARTY_CURRENT_POSITION")}
                        name="hienNayChucVuDang"
                        readOnly={isView || !formik.values?.dangVien}
                        value={formik.values.hienNayChucVuDang}
                        onChange={formik.handleChange}
                      />
                    </Col>
                    <Col xs={3}>
                      <TextValidator
                        className="input-politics"
                        lable={lang("INPUT.POLITICAL.DAY_OF_TRANSFER_TO_THE_PARTY_MINISTRY")}
                        name="hienNayNgayChuyenDenChiBoHienTai"
                        value={formik.values.hienNayNgayChuyenDenChiBoHienTai}
                        type="date"
                        readOnly={isView || !formik.values?.dangVien}
                        onChange={formik.handleChange}
                        onBlur={() =>
                          handleBlurDate(
                            formik.setFieldValue,
                            formik.values?.hienNayNgayChuyenDenChiBoHienTai,
                            "hienNayNgayChuyenDenChiBoHienTai"
                          )
                        }
                      />
                    </Col>
                  </Row>
                </Col>
              )}
            </Row>
            <Row>
              <Col xs={2}>
                <Form.Check
                  className="w-100 custom-form-check"
                  label={lang("INPUT.POLITICAL.LEFT_THE_PARTY")}
                  name="hienNayDaRoiDang"
                  disabled={isView || !formik.values?.dangVien}
                  checked={formik.values?.hienNayDaRoiDang}
                  onChange={handleChangeCheckBox}
                />
              </Col>
              {formik.values?.hienNayDaRoiDang && (
                <Col xs={10}>
                  <Row>
                    <Col xs={3}>
                      <TextValidator
                        className="input-politics"
                        lable={lang("INPUT.POLITICAL.DAY_OF_LEAVING_THE_PARTY")}
                        name="hienNayNgayRoiDang"
                        readOnly={isView || !formik.values?.dangVien}
                        type="date"
                        value={formik.values.hienNayNgayRoiDang}
                        onChange={formik.handleChange}
                        onBlur={() =>
                          handleBlurDate(
                            formik.setFieldValue,
                            formik.values?.hienNayNgayRoiDang,
                            "hienNayNgayRoiDang"
                          )
                        }
                      />
                    </Col>
                    <Col xs={3}>
                      <TextValidator
                        className="input-politics"
                        lable={lang("INPUT.POLITICAL.REASON_FOR_LEAVING_THE_PARTY")}
                        name="hienNayLyDoRoiDang"
                        readOnly={isView || !formik.values?.dangVien}
                        value={formik.values.hienNayLyDoRoiDang}
                        onChange={formik.handleChange}
                      />
                    </Col>
                  </Row>
                </Col>
              )}
            </Row>
          </Row>
        </div>
      </Form>
    </>
  );
};

export default PoliticsInformation;
